import React from 'react'
import { useHistory } from 'react-router-dom'
import { Flex, Box } from 'reflexbox'
import styled from 'styled-components'
import Spinner from 'ui-components/lib/components/Spinner'
import ProfileMenu from '../ProfileMenu'
import Notifications from '../Notifications'
import ThemeToggle from '../ThemeToggle'
import bnz_coin_icon from '../../images/coin.svg'
import { Menu, X } from 'lucide-react'


const Wrapper = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background-color: var(--bg-color);
  opacity: 0.95;
  border-bottom: 1px solid var(--border-color);
  height: 64px;
  padding: 0 24px;
  margin: 0;
  position: sticky;
  top: 0;
  z-index: 1000;
  backdrop-filter: blur(8px);
  transition: background-color 0.3s ease, border-color 0.3s ease;
  
  @media only screen and (max-width: 600px) {
    padding: 0 16px;
    z-index: 1000;
    position: relative;
  }
`

const BoxLeft = styled(Box)`
  display: flex; 
  align-items: center;
  justify-content: flex-start;
  gap: 12px;

  @media only screen and (max-width: 768px) {
    display: none!important;
  }
`

const PlanBadge = styled.div`
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  border: 1px solid rgba(241, 79, 92, 0.2);
  background-color: rgba(241, 79, 92, 0.05);
  color: #F14F5C;
  font-weight: 500;
  font-size: 14px;
  padding: 4px 12px;
`

const BoxCenter = styled(Box)`
  display: flex; 
  align-items: center; 
  justify-content: center;
  gap: 8px;

  @media only screen and (max-width: 768px) {
    justify-content: flex-start;
  }
`

const BoxRight = styled(Box)`
  display: flex; 
  align-items: center; 
  justify-content: flex-end;
  gap: 16px;

  @media only screen and (max-width: 600px) {
    display: none!important;
  }
`

const MobileMenuButton = styled.button`
  display: none;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 8px;
  color: var(--text-color);
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  transition: all 0.2s ease;
  flex-shrink: 0;
  min-width: 40px;
  min-height: 40px;
  
  &:hover {
    color: var(--red-bnz);
    background-color: rgba(241, 79, 92, 0.1);
    border-radius: 8px;
  }
  
  &:active {
    opacity: 0.7;
    transform: scale(0.95);
  }
  
  @media only screen and (max-width: 600px) {
    display: flex !important;
    visibility: visible !important;
    opacity: 1 !important;
    position: relative;
    z-index: 1000;
  }
`

const MobileRightSection = styled(Box)`
  display: none;
  
  @media only screen and (max-width: 600px) {
    display: flex;
    align-items: center;
    gap: 12px;
  }
`

const BnzCoin = styled.img`
  width: 28px;
  height: 28px;
  box-sizing: border-box;
`

const FakeButton = styled.button`
  border: none;
  cursor: pointer;
  background: transparent;
`

const CustomText = styled.h4`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  color: var(--text-color);
  text-transform: none;
  margin: 0;
  transition: color 0.3s ease;
`

const NavHeader = React.memo((props) => {
    const history = useHistory()
    
    // Aplica o tema imediatamente para evitar flash de fundo branco
    React.useLayoutEffect(() => {
      const isDark = document.body.classList.contains('dark') || document.documentElement.classList.contains('dark')
      const navHeader = document.querySelector('[data-nav-header]')
      if (navHeader) {
        if (isDark) {
          navHeader.style.backgroundColor = 'hsl(215, 35%, 12%)'
        } else {
          navHeader.style.backgroundColor = '#FFFFFF'
        }
      }
    })
    
    const isLoading = props.plansLoading || props.dataPrivacyLoading

    const planBadges = []
    const bonuzSub = props.dataBonuzPlan?.subscription
    if (bonuzSub?.active) {
      const id = bonuzSub.planIdentifier
      if (id === 'plano-essencial') planBadges.push('Plano Essencial')
      else if (id === 'plano-premium') planBadges.push('Plano Premium')
      else if (id === 'plano-platinum') planBadges.push('Plano Platinum')
      else if (id === 'plano-start') planBadges.push('Plano Start')
    }
    const dpSub = props.dataPrivacy?.subscription
    if (dpSub?.active) {
      if (dpSub.planIdentifier === 'data-privacy-semestral') planBadges.push('Plano Data Privacy Semestral')
      else if (dpSub.planIdentifier === 'data-privacy-anual') planBadges.push('Plano Data Privacy Anual')
    }
    if (planBadges.length === 0) {
      planBadges.push('Plano Freemium')
    }
    return (
      <Wrapper data-nav-header>
        {isLoading ? (
          <div style={{ 
            width: '100%', 
            height: '100%', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center' 
          }}>
            <Spinner color="primary" />
          </div>
        ) : (
          <>
            <MobileMenuButton 
              onClick={props.onToggleMenu} 
              aria-label={props.menuIsOpen ? "Fechar menu" : "Abrir menu"}
              type="button"
              style={{
                zIndex: 1000,
                position: 'relative'
              }}
            >
              {props.menuIsOpen ? <X size={24} strokeWidth={2} /> : <Menu size={24} strokeWidth={2} />}
            </MobileMenuButton>
            <BoxLeft style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
              {planBadges.map((label, idx) => (
                <PlanBadge key={`${label}-${idx}`}>{label}</PlanBadge>
              ))}
            </BoxLeft>
            <BoxRight>      
              {props.dataBonuzPlan?.subscription?.planIdentifier !== 'plano-premium' && props.dataBonuzPlan?.subscription?.planIdentifier !== 'plano-platinum' && (    
                <>
                  <BoxCenter>
                    <FakeButton onClick={() => history.push('/dashboard/carteira')} style={{display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 8px', borderRadius: '4px'}}>
                      <BnzCoin src={bnz_coin_icon} alt="Moeda B.onuz" />
                      <CustomText>{props.bnzBalance} BNZ</CustomText>
                    </FakeButton>  
                  </BoxCenter>
                  <div className='vertical-divider'></div>
                </>
              )}
              <ThemeToggle />
              <Notifications menuItems={props.profileMenuItems} style={{width: 'auto', justifyContent: 'center'}} />
              <ProfileMenu 
                menuItems={props.profileMenuItems} 
                style={{justifyContent: 'center'}}
                title={ props.clientName ? props.clientName : '' }
              />
            </BoxRight>
            <MobileRightSection>
              <ThemeToggle />
              <Notifications menuItems={props.profileMenuItems} style={{width: 'auto', justifyContent: 'center'}} />
              <ProfileMenu 
                menuItems={props.profileMenuItems} 
                style={{justifyContent: 'center'}}
                title={ props.clientName ? props.clientName : '' }
              />
            </MobileRightSection>
          </>
        )}
      </Wrapper>
    )
})

NavHeader.displayName = 'NavHeader'

export default NavHeader
